import type { AggressionProfile } from "./settings";

/**
 * Pure session-frequency guardrails. Given how many units a viewer has already
 * seen this session and their aggression profile, decides whether one more may
 * show — this is the "aggressive but not annoying" dial, codified rather than
 * vibes. The counts are stored client-side (sessionStorage); this module is
 * pure so it is fully unit-testable.
 */

export type UnitKind = "display" | "affiliate";

export type GuardrailDecision = {
	allowed: boolean;
	/** Why it was blocked, for telemetry. */
	reason?: "session_cap" | "display_cap" | "affiliate_cap";
};

export type SessionCounts = {
	display: number;
	affiliate: number;
};

export function canShowUnit(args: {
	profile: AggressionProfile;
	kind: UnitKind;
	counts: SessionCounts;
	/**
	 * A dedicated budget for one surface (e.g. `job_detail_after_apply`),
	 * independent of the shared `counts.affiliate` pool. When set, this
	 * REPLACES the shared `maxAffiliate` check for this call — the surface
	 * gets its own counter/cap instead of competing with every other surface
	 * for `profile.maxAffiliate`. The overall `sessionImpressionCap` (based on
	 * `counts`, which still includes this surface's impressions once shown)
	 * applies regardless.
	 */
	reserved?: { count: number; cap: number };
}): GuardrailDecision {
	const { profile, kind, counts, reserved } = args;
	const total = counts.display + counts.affiliate;

	if (total >= profile.sessionImpressionCap) {
		return { allowed: false, reason: "session_cap" };
	}
	if (kind === "display" && counts.display >= profile.maxDisplay) {
		return { allowed: false, reason: "display_cap" };
	}
	if (kind === "affiliate") {
		const capped = reserved
			? reserved.count >= reserved.cap
			: counts.affiliate >= profile.maxAffiliate;
		if (capped) {
			return { allowed: false, reason: "affiliate_cap" };
		}
	}
	return { allowed: true };
}

/**
 * A dismissed unit subtracts from its arm's effective score so a creative the
 * viewer actively rejected is shown less. Returns the (non-negative) penalty to
 * apply to the running click-prior numerator.
 */
export function dismissPenalty(weight: number): number {
	return weight > 0 ? weight : 0;
}
